import React, { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "react-hot-toast";

const API_BASE = process.env.REACT_APP_API_BASE;

const RideDriverModal = ({ worker, onClose }) => {
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);

  if (!worker?.driverProfile) return null;

  const { make, model, year, licenseUrl, insuranceUrl, carPhotoUrl, status } =
    worker.driverProfile;

  // ============================================================
  // 🟢 STATUS DISPLAY
  // ============================================================
  const renderStatus = (status) => {
    if (status === "approved")
      return <span className="text-green-600 font-semibold">Approved ✅</span>;
    if (status === "pending")
      return <span className="text-yellow-600 font-semibold">Pending ⏳</span>;
    if (status === "rejected")
      return <span className="text-red-600 font-semibold">Rejected ❌</span>;
    return <span className="text-gray-600">N/A</span>;
  };

  // ============================================================
  // 🔄 UPDATE STATUS
  // ============================================================
  const updateRideStatus = async (value) => {
    if (value === "rejected" && !reason.trim()) {
      toast.error("Enter a reason before rejecting.");
      return;
    }

    try {
      setLoading(true);
      const payload =
        value === "rejected" ? { status: value, reason } : { status: value };

      await axios.post(`${API_BASE}/admin/update-ride-docs/${worker._id}`, payload);
      toast.success(`🚗 Ride documents marked as ${value}`);
      onClose();
    } catch (err) {
      console.error("❌ Ride doc update failed", err);
      toast.error("Failed to update ride document status.");
    } finally {
      setLoading(false);
    }
  };

  // ============================================================
  // ⛔ REVOKE ACCESS
  // ============================================================
  const handleRevokeAccess = async () => {
    if (!window.confirm("Are you sure you want to revoke ride access for this driver?"))
      return;

    try {
      setLoading(true);
      await axios.post(`${API_BASE}/admin/revoke-ride-access/${worker._id}`);
      toast.success("🚫 Ride access has been revoked.");
      onClose();
    } catch (err) {
      console.error("❌ Revoke failed:", err);
      toast.error("Failed to revoke ride access.");
    } finally {
      setLoading(false);
    }
  };

  // ============================================================
  // 🔗 FULL URL GENERATOR
  // ============================================================
const fullUrl = (url) => {
  if (!url) return "#";

  // Detect if it’s already a full URL
  if (url.startsWith("http")) return url;

  // Ensure API_BASE exists and remove trailing /api if present
  const apiHost = API_BASE.replace(/\/api\/?$/, "");

  // Ensure there's a leading slash for relative paths
  const fixedUrl = url.startsWith("/") ? url : `/${url}`;

  // Build absolute URL safely
  return `${apiHost}${fixedUrl}`;
};

  // ============================================================
  // 🖥️ RENDER MODAL
  // ============================================================
  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <motion.div
          className="bg-white w-full max-w-3xl rounded-2xl p-6 overflow-y-auto max-h-[90vh] shadow-2xl font-[Poppins]"
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
        >
          {/* Header */}
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold text-gray-800">
              🚘 Ride Driver Documents
            </h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-red-600 text-lg font-semibold"
            >
              ✕
            </button>
          </div>

          {/* Driver Info */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm mb-6">
            <p>
              <strong>Name:</strong> {worker.name}
            </p>
            <p>
              <strong>Email:</strong> {worker.email}
            </p>
            <p>
              <strong>Vehicle:</strong> {year} {make} {model}
            </p>
            <p>
              <strong>Status:</strong> {renderStatus(status)}
            </p>
          </div>

          {/* Documents */}
          <div className="space-y-4 text-sm">
            <div>
              <p className="font-semibold mb-1">📄 License</p>
              <a
                href={fullUrl(licenseUrl)}
                target="_blank"
                rel="noreferrer"
                className="text-blue-600 underline"
              >
                View License
              </a>
            </div>
            <div>
              <p className="font-semibold mb-1">📝 Insurance</p>
              <a
                href={fullUrl(insuranceUrl)}
                target="_blank"
                rel="noreferrer"
                className="text-blue-600 underline"
              >
                View Insurance
              </a>
            </div>
            <div>
              <p className="font-semibold mb-1">🚗 Car Photo</p>
              <a
                href={fullUrl(carPhotoUrl)}
                target="_blank"
                rel="noreferrer"
                className="text-blue-600 underline"
              >
                View Car Photo
              </a>
            </div>
          </div>

          {/* Reason (only if rejecting) */}
          {status !== "approved" && (
            <div className="mt-4">
              <textarea
                rows={2}
                className="w-full border p-2 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                placeholder="Enter reason (if rejecting)"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </div>
          )}

          {/* Actions */}
          <div className="mt-6 flex flex-wrap justify-end gap-3">
            <button
              onClick={() => updateRideStatus("approved")}
              disabled={loading}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-semibold transition"
            >
              ✅ Approve
            </button>
            <button
              onClick={() => updateRideStatus("rejected")}
              disabled={loading}
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-semibold transition"
            >
              ❌ Reject
            </button>
            <button
              onClick={handleRevokeAccess}
              disabled={loading}
              className="bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded-lg font-semibold transition"
            >
              🚫 Revoke Access
            </button>
            <button
              onClick={onClose}
              className="bg-gray-300 hover:bg-gray-400 px-4 py-2 rounded-lg font-semibold text-gray-800"
            >
              Close
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default RideDriverModal;
